import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import { FaAlignLeft } from "react-icons/fa";
import {
  Button,
  Input,
  message,
  Segmented,
  Space,
  Table,
  TableColumnsType,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import type { InputRef } from "antd";
import type { ColumnType, TablePaginationConfig } from "antd/es/table";
import type { FilterConfirmProps, FilterValue } from "antd/es/table/interface";
import styles from "./CustomersTable.module.scss";
import UserManagementFunction from "../../Functions/UserManagement/UserManagement";
import MegaphoneModal from "../MegaphoneModal/MegaphoneModal";
import { closeCustomersModal } from "../../Redux/SuperAdminModal/modalSlice";
import { setCustomer } from "../../Redux/Customer/customerSlice";
import { changeCustId } from "../../Redux/User/userSlice";
import colors from "../../Utils/colors";
import { isMobileDevice } from "../../Utils/Utils";
import Loader from "../GlobalLoader/Loader";
import "../../App.scss";

// Interface for customer data
interface Customer {
  id: number;
  name: string;
  description: string;
  accountId: string;
}

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

interface CustomerManagementProps {
  isClosable?: boolean;
}

const CustomerChooser: React.FC<CustomerManagementProps> = ({
  isClosable = true,
}) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const accessToken = useSelector(
    (state: RootState) => state.access.accessToken
  );
  const sessionToken = useSelector((state: RootState) => state.user.token);
  const userData = useSelector((state: RootState) => state.user.user);
  const dispatch = useDispatch();

  const isVisible = useSelector(
    (state: RootState) => state.customersModal.isOpen
  );

  // Contract type: all, cgs, non-cgs
  const [contractType, setContractType] = useState("all");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      position: ["bottomCenter"],
    },
  });
  const [messageApi, contextHolder] = message.useMessage();
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: any
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (
    clearFilters: () => void,
    dataIndex: any,
    confirm: any
  ) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const getColumnSearchProps = (dataIndex: any): ColumnType<any> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, dataIndex, confirm)
            }
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined
        style={{ color: filtered ? colors.colorPrimary : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes(value?.toString().toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: TableColumnsType<any> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
      ...getColumnSearchProps("id"),
      sorter: true,
      defaultSortOrder: "ascend",
    },
    {
      title: "Contract Number",
      dataIndex: "accountId",
      key: "accountId",
      width: "20%",
      ...getColumnSearchProps("accountId"),
      sorter: true,
      responsive: ["md", "lg"],
    },
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      ...getColumnSearchProps("name"),
      sorter: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      responsive: ["lg"],
    },
  ];

  const handleSwitchCustomer = (record: any) => {
    dispatch(changeCustId(record.id));
    dispatch(setCustomer(record));
    handleCloseModal();
  };

  const handleCloseModal = () => {
    dispatch(closeCustomersModal());
  };

  const fetchCustomers = (
    tableParams: TableParams,
    contractType: string = "all"
  ) => {
    let tempData: Customer[] = [];
    const params = {
      page: tableParams.pagination?.current?.toString(),
      pageSize: tableParams.pagination?.pageSize?.toString(),
      type: contractType,
      orderBy: tableParams.sortField,
      orderDir: tableParams.sortOrder,
      ...tableParams.filters,
    };
    // Filter out null or undefined values from the params
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== null && value !== undefined
      )
    );
    setLoading(true);
    UserManagementFunction.getAllCustomers(
      accessToken!!,
      sessionToken!!,
      filteredParams
    )
      .then((response) => {
        if (
          response.status === true &&
          response.statusCode === 200 &&
          response?.data
        ) {
          const responseData = response.data.customer;
          responseData.forEach((data: any) => {
            tempData.push(data);
          });
          setCustomers(tempData);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: response?.data.totalRows,
            },
          });
        } else {
          const message =
            response.message || "An error occurred getting all customers.";
          messageApi.error(message, 2.5);
        }
      })
      .catch((e: any) => {
        console.error(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (userData?.loginType === "super")
      fetchCustomers(tableParams, contractType);
  }, [contractType, JSON.stringify(tableParams)]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    let sort;
    if (sorter && sorter.column) {
      sort = {
        sortField: sorter.column.dataIndex,
        sortOrder: sorter.order === "ascend" ? "asc" : "desc",
      };
    }
    setTableParams({
      pagination,
      filters,
      ...sort,
    });
  };

  const handleChangeContractType = (value: string) => {
    setSelectedCustomer(null);
    setContractType(value);
  };

  return (
    <MegaphoneModal
      modalKey={"choose-customer"}
      title={"Select Customer"}
      icon={FaAlignLeft}
      visible={isVisible}
      onClose={handleCloseModal}
      width={1000}
      footer={
        <Space direction={"horizontal"}>
          <Button type={"default"} onClick={handleCloseModal}>
            Close
          </Button>
          <Button
            type={"primary"}
            onClick={() => {
              console.log(selectedCustomer);
              handleSwitchCustomer(selectedCustomer);
            }}
          >
            Select
          </Button>
        </Space>
      }
      closable={isClosable}
      customClassNames={{ body: styles.modalBody }}
      headerRight={
        <div className="d-flex justify-content-end">
          <Segmented
            className="custom-segmented"
            value={contractType}
            size={isMobileDevice() ? "small" : "middle"}
            onChange={(value: any) => handleChangeContractType(value)}
            options={[
              {
                label: "All",
                value: "all",
              },
              {
                label: "CGS",
                value: "cgs",
              },
              {
                label: "NON-CGS",
                value: "non-cgs",
              },
            ]}
          />
        </div>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Table
          rowKey={"id"}
          columns={columns}
          dataSource={customers}
          size={isMobileDevice() ? "small" : "middle"}
          scroll={{ y: 600 }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                setSelectedCustomer(record);
              },
              onDoubleClick: (event) => {
                handleSwitchCustomer(record);
              },
            };
          }}
          rowClassName={(record) => {
            return record === selectedCustomer
              ? styles.selectedRow
              : styles.normalRow;
          }}
          pagination={{
            ...tableParams.pagination,
          }}
          onChange={handleTableChange}
          loading={{
            spinning: loading,
            indicator: (
              <div>
                <Loader blue={true} size={"sm"} backgroundWeb={false} />
              </div>
            ),
          }}
        />
      </Space>
      {contextHolder}
    </MegaphoneModal>
  );
};

export default CustomerChooser;
